import { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/formatters";
import {
  filterSortHealth,
  paginate,
  type HealthCol,
  type SortDir,
} from "@/lib/msp-list";
import type { MspIntegrationHealth } from "@/types/api";

interface MspHealthTableProps {
  integrations: MspIntegrationHealth[];
  isLoading: boolean;
}

const healthColors: Record<string, string> = {
  healthy: "text-green-400",
  unhealthy: "text-red-400",
  disabled: "text-muted-foreground",
  unknown: "text-yellow-400",
};

const healthDot: Record<string, string> = {
  healthy: "bg-green-500",
  unhealthy: "bg-red-500",
  disabled: "bg-gray-500",
  unknown: "bg-yellow-500",
};

const PAGE_SIZE = 15;

export function MspHealthTable({ integrations, isLoading }: MspHealthTableProps) {
  const [search, setSearch] = useState("");
  const [sortCol, setSortCol] = useState<HealthCol>("tenant_name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [search, sortCol, sortDir]);

  // Memoized above the early returns so hook order stays stable.
  const filtered = useMemo(
    () => filterSortHealth(integrations, search, sortCol, sortDir),
    [integrations, search, sortCol, sortDir],
  );
  const { pageItems, totalPages, safePage } = useMemo(
    () => paginate(filtered, page, PAGE_SIZE),
    [filtered, page],
  );

  const toggleSort = (col: HealthCol) => {
    if (col === sortCol) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
  };

  const SortHead = ({
    col,
    label,
    className,
  }: {
    col: HealthCol;
    label: string;
    className?: string;
  }) => (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => toggleSort(col)}
        className="inline-flex items-center gap-1 hover:text-foreground"
        aria-label={`Sort by ${label}`}
      >
        {label}
        {sortCol === col &&
          (sortDir === "asc" ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          ))}
      </button>
    </TableHead>
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
        No integration health data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search integrations…"
        aria-label="Search integrations"
        className="h-9 w-64"
      />
      {filtered.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          No integrations match your search
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <SortHead col="tenant_name" label="Tenant" />
                <SortHead col="source_type" label="Source" />
                <TableHead className="hidden md:table-cell">Name</TableHead>
                <SortHead col="health_status" label="Status" />
                <SortHead
                  col="consecutive_failures"
                  label="Failures"
                  className="text-right"
                />
                <SortHead
                  col="last_poll_at"
                  label="Last Poll"
                  className="text-right"
                />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((item) => (
                <TableRow key={`${item.tenant_id}-${item.integration_id}`}>
                  <TableCell className="font-medium">
                    {item.tenant_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.source_type}
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground md:table-cell">
                    {item.display_name}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span
                        className={cn(
                          "inline-block h-2 w-2 rounded-full",
                          healthDot[item.health_status] ?? "bg-gray-500",
                        )}
                      />
                      <span
                        className={cn(
                          "text-xs font-medium capitalize",
                          healthColors[item.health_status] ??
                            "text-muted-foreground",
                        )}
                      >
                        {item.health_status}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-right text-sm tabular-nums",
                      item.consecutive_failures > 0
                        ? "text-red-400"
                        : "text-muted-foreground",
                    )}
                  >
                    {item.consecutive_failures}
                  </TableCell>
                  <TableCell className="text-right text-sm text-muted-foreground">
                    {item.last_poll_at
                      ? formatRelativeTime(item.last_poll_at)
                      : "Never"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Showing {pageItems.length} of {filtered.length} integrations
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage <= 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span>
                  Page {safePage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage >= totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
